import cloneDeep from 'lodash.clonedeep'

export const openLink = (link) => {
  try {
    if (window?.webkit?.messageHandlers?.externalUrlLoader?.postMessage) {
      window.webkit.messageHandlers.externalUrlLoader.postMessage(link)
    } else if (window?.Android?.redirectToExternalLink) {
      window.Android.redirectToExternalLink(link)
    } else {
      window.open(link)
    }
  } catch (error) {
    console.error(error);
  }
}

export const updateObject = (firstObject, secondObject) => {
  return Object.assign({}, firstObject, secondObject)
}

export const getMaxX = (data) => {
  return data.reduce((max, p) => p.x > max ? p.x : max, data[0].x)
}

export const getMaxY = (data) => {
  return data.reduce((max, p) => p.y > max ? p.y : max, data[0].y)
}

export const zeroFill = (number, width) => {
  width -= number.toString().length
  if (width > 0) {
    return new Array(width + (/\./.test(number) ? 2 : 1)).join('0') + number
  }
  return number + ''
}

export const promiseWithTimeout = (
  promise,
  timeout = 1000,
  extraInfo = {}
) => {
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      setTimeout(() => {
        let toLog = ''
        const keys = Object.keys(extraInfo)

        if (keys.length) {
          toLog = '\n' + keys.map(key => {
            return `${key}: ${extraInfo[key]}`
          }).join('\n')
        }

        reject(new Error(`Timed out after ${timeout}ms.${toLog}`))
      }, timeout)
    })
  ])
}

export const fetchWithTimeout = (url, options, timeout) => {
  return promiseWithTimeout(
    fetch(url, options),
    timeout,
    {
      url
    }
  )
}

export const appendScript = (src, cleanup = false) => {
  if (cleanup) {
    forEach(document.querySelectorAll(`[src="${src}"]`), (script) => {
      script.remove()
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.addEventListener('load', (e) => resolve(e))
    script.addEventListener('error', (e) => reject(e))
    document.head.appendChild(script)
  })
}

/*
get global object
*/
/* eslint-disable */
const tempGlobal = {}
export const getGlobal = () => typeof globalThis !== 'undefined' ? globalThis : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : typeof window !== 'undefined' ? window : tempGlobal
/* eslint-enable */

/*
  Returns comma separated numbers
*/

export const commaSeparated = (value) => {
  return Number(value).toLocaleString('en')
}

/*
  Returns a number with its unit or a default if not a number
*/

export const numberWithUnit = (number, unit, notAnumber = 'N/A') => {
  return (typeof number === 'number') ? `${commaSeparated(number)}${unit ? ` ${unit}` : ''}` : notAnumber
}

/*
Given a string value, it returns its quantity and unit. If a number is passed, it will just return the number and default the quantity to px.

i.e. getQuantityAndUnit('10rem') === { quantity: 10, unit: 'rem' }
*/

export const getQuantityAndUnit = (value) => {
  if (!(['string', 'number'].includes(typeof value))) {
    throw new Error('Must be a string or a number')
  }

  if (typeof value === 'number') {
    return {
      quantity: value,
      unit: 'px'
    }
  }

  const unit = value.replace(/[0-9.]+/gi, '')
  const quantity = Number(value.replace(unit, ''))

  return {
    quantity,
    unit
  }
}

/*
Converts an array to an object.
i.e. objectFromArray(['zero', { one: true }]) === { 0: 'zero', 1: { one: true } }
*/

export const objectFromArray = (value) => {
  if (typeof value !== 'object') throw new Error('It must be an array or an object')

  if (Array.isArray(value)) {
    return value.reduce((object, el, i) => {
      object[i] = el
      return object
    }, {})
  } else {
    return value
  }
}

export const hash = (string) => {
  if (typeof string !== 'string') {
    throw new Error('Argument must be a string!')
  }

  let hash = 0
  let i
  let char

  for (i = 0; i < string.length; i++) {
    char = string.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash |= 0 // Convert to 32bit integer
  }

  return hash
}

/*
Returns a function, that, as long as it continues to be invoked, will not be triggered. The function will be called after it stops being called for N milliseconds. If `immediate` is passed, trigger the function on the leading edge, instead of the trailing.
*/

export const debounce = (func, wait, immediate) => {
  let timeout
  return (...args) => {
    const later = () => {
      timeout = null
      if (!immediate) func.apply(this, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(this, args)
  }
}

/*
Returns a function that will be fired again only if the time limit has passed.
It takes fn, which is the function you want to be throttled, and limit, which is the time you want to enforce it to wait before being fired again.
*/

export const throttle = (func, limit, finalCall) => {
  let waiting = false
  let finalCallTimeout = null
  return (...args) => {
    finalCall && clearTimeout(finalCallTimeout)
    if (!waiting) {
      func.apply(this, args)
      waiting = true
      setTimeout(() => {
        waiting = false
      }, limit)
    } else {
      finalCallTimeout = finalCall && setTimeout(() => {
        func.apply(this, args)
      }, limit)
    }
  }
}

/*
 * It allows to grab nested properties of an object by passing a string containing the names of property or return undefined.
 * i.e. const nestedObject = {
   prop: {
     prop1: {
       prop2: ['prop']
     }
   }
 }
 getNestedProp(nestedObject, 'prop.prop1.prop2[0]') = 'prop'
 getNestedProp(nestedObject, 'prop.prop1.prop2.prop3') = undefined
*/

export const getNestedProp = (targetObject, path, defaultIfUndefined) => {
  let currentValue = targetObject

  try {
    /* eslint-disable */
    const keys = path.split(/[\[\].]+/).filter(_ => _)
    /* eslint-enable */

    keys
      .map((key) => {
        currentValue = currentValue[key] !== undefined ? currentValue[key] : defaultIfUndefined
      })
  } catch (e) {
    currentValue = defaultIfUndefined
  }
  return currentValue
}

export const get = getNestedProp

/**
 * Dynamically sets a deeply nested value in an object.
 * Optionally "bores" a path to it if its undefined.
 * @function
 * @param {!object} targetObject  - The object which contains the value you want to change/set.
 * @param {!string} path  - The string representation of path to the value you want to change/set.
 * @param {!mixed} value - The value you want to set it to.
 * @param {boolean} createNew - If true, will set value of non-existing path as well.
 */
export const setNestedProp = (targetObject, path, value, createNew = false) => {
  /* eslint-disable */
  const keys = path.split(/[\[\].]+/).filter(_ => _)
  /* eslint-enable */

  keys
    .reduce((target, key, i) => {
      if (createNew && typeof target[key] === 'undefined' && i !== keys.length - 1) {
        target[key] = {}
        return target[key]
      }

      if (i === keys.length - 1) {
        target[key] = value
        return value
      } else {
        return target[key]
      }
    }, targetObject)
}

/*
 * It allows looping through arrays and nodes in all browsers
 * i.e. forEach(array, (arrayElement, index) => {})
 */
export const forEach = (array, callback) => {
  if (!array || !callback) return
  for (let i = 0; i < array.length; i++) {
    callback(array[i], i)
  }
}

/*
Passing a mouse or touch event, it will return the coordinates for that event.

i.e. getCoordsFromEvent(e) === { x: 150, y: 150 }
*/

export const getCoordsFromEvent = (e) => {
  let coords = {
    x: e.clientX || 0,
    y: e.clientY || 0
  }

  if (e.touches || e.targetTouches || e.changedTouches) {
    const touch = [e.touches, e.targetTouches, e.changedTouches]
      .filter(touches => {
        return touches.length
      })[0][0]

    coords = {
      x: touch.clientX,
      y: touch.clientY
    }
  }

  return coords
}

/*
 * Finds the first ancestor of the element that matches a specific cssSelector
 * i.e. findAncestor(document.querySelector('div'), '.divs-parent')
 */
export const findAncestor = (el, cssSelector) => {
  while ((el = el.parentElement) && !(el.matches || el.matchesSelector).call(el, cssSelector));
  return el
}

/*
 * Finds the first ancestor of the element that matches a specific computedStyle
 * i.e. findAncestorWithStyle(document.querySelector('div'), 'display', 'block')
 */
export const findAncestorWithStyle = (el, cssProp, cssPropValue) => {
  if (!el || !cssProp || !cssPropValue) return null
  while ((el = el.parentElement) && !(getComputedStyle(el)[cssProp] === cssPropValue));
  return el
}

/*
 * Finds the next sibling that the condition function will return true for, or, if missing, just the next sibling
 * i.e. find the next div sibling: findSibling(myDiv, nextSibling => nextSibling.nodeName === 'div')
 */

export const findSibling = (el, condition) => {
  const {
    nextElementSibling,
    nextSibling
  } = el
  const nextEl = nextElementSibling || nextSibling

  if (!nextEl || !condition || typeof condition !== 'function') {
    return nextEl
  }

  if (condition(nextEl)) {
    return nextEl
  }

  return findSibling(nextEl, condition)
}

/*
 * It inserts a newElement after the specified target
 * i.e. insertAfter(document.querySelector('.insert-after-this'), document.createElement('div'))
 */
export const insertAfter = (target, newElement) => {
  target.parentNode.insertBefore(newElement, target.nextSibling)
}

/*
 * Checks if the element is fully in the viewport
 */

export const isElementFullyInViewport = (el) => {
  try {
    const rect = el.getBoundingClientRect()

    return (
      rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  } catch (error) {
    return false
  }
}

/*
 * It returns non-empty paths in an array, either from a provided string or location.pathname
 * i.e. /test/stuff => ['test', 'stuff']
 */

export const getPaths = (pathname) => {
  if (typeof window !== 'undefined') {
    pathname = pathname || location.pathname
  }

  try {
    return pathname.split('/').filter(_ => _)
  } catch {
    // noop
  }
}

/*
 * It returns an object with all the query params in any url, defaults to current url
 * i.e. queryParams('https://google.com/?key=value&key2=false&key3=3')
 * expected: {
 key: 'value',
 key2: false,
 key3: 3
}
*/
export const queryParams = (url = location.search) => {
  const i = url.indexOf('?') >= 0 ? url.indexOf('?') : url.length

  return url
    .substring(i)
    .slice(1)
    .split('&')
    .map(p => p.split('='))
    .reduce((obj, pair) => {
      let [key, value] = pair.map(decodeURIComponent)
      if (!key) return { ...obj }

      if (value === 'true' || !value) {
        value = true
      } else if (value === 'false') {
        value = false
      } else if (Number(value)) {
        value = Number(value)
      }

      return { ...obj, [key]: value }
    }, {})
}

export const hasOwnProperty = (object, key) => {
  return Object.prototype.hasOwnProperty.call(object, key)
}

/*
 * Returns a string of query params from an object already formatted and encoded.
 * i.e. buildQueryParams({ search: 'stuff', limit: 1 })
 * returns '?search=stuff&limit=1'
 * Pro tip: use it in conjuction with queryParams() to merge the existing params
 buildQueryParams(
   Object.assign({},
     queryParams(),
     { search: 'stuff', limit: 1 }
   )
 )
*/

export const buildQueryParams = object => {
  const params = []

  try {
    for (const key in object) {
      if (hasOwnProperty(object, key)) {
        params.push(`${(key)}=${(object[key])}`)
      }
    }
    return `?${params.join('&')}`
  } catch (e) {
    return ''
  }
}

/*
 * A class to modify, convert and manage query params
 */

export class QueryParams {
  constructor (params = (typeof location !== 'undefined' ? location.search : null)) {
    switch (typeof params) {
    case 'string': {
      this.text = params
      break
    }

    case 'object': {
      this.object = params
      break
    }

    default: break
    }
  }

  get text () { return this._text }

  set text (value) {
    this._text = buildQueryParams(queryParams(value))
    this._object = queryParams(value)
    // eslint-disable-next-line no-setter-return
    return this
  }

  get object () { return this._object }

  set object (value) {
    this._object = value
    this._text = buildQueryParams(value)
    // eslint-disable-next-line no-setter-return
    return this
  }

  set = (...args) => {
    if (typeof args[0] === 'object') {
      const params = args[0]
      this.object = params
      this.text = buildQueryParams(params)
    } else {
      const [name, value] = args
      this.object[name] = value
      this.text = buildQueryParams(this.object)
    }

    return this
  }

  remove = (name) => {
    const copyObject = { ...this.object }
    delete copyObject[name]
    this.object = copyObject
    this.text = buildQueryParams(this.object)
  }
}

/*
 * Strips the HTML from a string. Works only in the browser.
 * i.e. stripHTML('<p>I am your <strong>father</strong>!</p>')
 * returns 'I am your father!'
 */

export const stripHTML = string => {
  if (document && document.createElement) {
    const el = document.createElement('div')
    el.innerHTML = string
    string = el.innerText
  }
  return string
}

export const setOmniStorageItems = (user, hosts) => {
  omniStorage.set('KEYBOARD_LAYOUT', getNestedProp(user, 'settings.keyboard_layout'))
  hosts[0] && Object.keys(hosts[0]).map(key => {
    omniStorage.set(`HOST_${key.toUpperCase()}`, hosts[0][key])
  })
}

export const getCookies = (name = null) => {
  let cookies
  if (typeof window !== 'undefined' && document.cookie && document.cookie !== '') {
    cookies = document.cookie.split(';')
    const cookiesObject = {}
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim().split('=', 2)
      cookiesObject[cookie[0]] = cookie[1]
      if (name && name === cookie[0]) {
        break
      }
    }
    cookies = cookiesObject
  }
  return (name && cookies) ? cookies[name] : cookies
}

/*
 * Gets a cookie with given name
 * i.e. getCookie('monster')
 */
export const getCookie = getCookies

/*
 * Sets a cookie with given name, value and expiration in days
 * i.e. setCookie('cookie', 'monster', 365)
 */
export const setCookie = (cookieName, cookieValue, expirationDays = 365) => {
  const now = new Date()
  now.setTime(now.getTime() + expirationDays * 24 * 60 * 60 * 1000)
  const expires = `expires=${now.toUTCString()}`
  document.cookie = `${cookieName}=${cookieValue};${expires};path=/`
}

export const removeCookie = (cookieName) => {
  document.cookie = cookieName + '=; Max-Age=-99999999;'
}

/*
 * It returns a deep clone of the object or null
 * i.e. object = {a: 'a'}
 * object.b = deepClone(object)
 * expected object = {
 a: 'a',
 b: {
 a: 'a'
}
}
*/
export const deepClone = (object) => cloneDeep(object)

export const DEPRECATED__deepClone = object => {
  let clone = null

  try {
    if (Notification && location.protocol.indexOf('https') >= 0) {
      clone = new Notification('', { data: object, silent: true }).data
      return clone
    }
  } catch (e) {
    console.warn(e.message)
    clone = null
  }

  try {
    if (history && history.replaceState) {
      const oldState = history.state
      history.replaceState(object, document.title)
      clone = history.state
      history.replaceState(oldState, document.title)
      return clone
    }
  } catch (e) {
    console.warn(e.message)
    clone = null
  }

  try {
    clone = JSON.parse(JSON.stringify(object))
  } catch (e) {
    console.warn(e.message)
    clone = null
  }
  // Taken from https://github.com/whatwg/html/issues/793, it's async, me no likey
  // let promise = new Promise(resolve => {
  //   const { port1, port2 } = new MessageChannel()
  //   port2.onmessage = (ev) => {
  //     resolve(ev.data)
  //   }
  //   port1.postMessage(object)
  // })
  // clone = await promise
  return clone
}

class StructuredCloner {
  constructor () {
    this.pendingClones_ = new Map()
    this.nextKey_ = 0

    const channel = new MessageChannel()
    this.inPort_ = channel.port1
    this.outPort_ = channel.port2

    this.outPort_.onmessage = ({ data: { key, value } }) => {
      const resolve = this.pendingClones_.get(key)
      resolve(value)
      this.pendingClones_.delete(key)
    }
    this.outPort_.start()
  }

  cloneAsync (value) {
    return new Promise(resolve => {
      const key = this.nextKey_++
      this.pendingClones_.set(key, resolve)
      this.inPort_.postMessage({ key, value })
    })
  }
}

const deepCloner = (typeof window !== 'undefined') ? StructuredCloner.prototype.cloneAsync.bind(new StructuredCloner()) : () => { throw new Error('Not in browser.') }

export const DEPRECATED__deepCloneAsync = async (object) => {
  try {
    return (await deepCloner(object))
  } catch (error) {
    return deepClone(object)
  }
}

/*
Makes the execution wait for the given amount of milliseconds in an async/await or promise environment. Works with chaining and passing the next value
i.e. async () => {
  await wait(1000)
  getData()
    .then(data => wait(500, data))
    .then(dataAfterWaiting => doStuff(dataAfterWaiting))
}
*/

export const wait = (milliseconds = 500, then = () => null) => {
  return (new Promise(resolve => {
    setTimeout(() => {
      resolve()
    }, milliseconds)
  })).then(then)
}

export const toArray = nodes => {
  try {
    return [].slice.call(nodes)
  } catch (e) {
    return []
  }
}

/*
It returns the object with the keys reordered. It is case sensitive.
i.e. orderObjectKeys({B:'B',a:'a',b:'b',A:'A', 2:2,10:10})
returns {2: 2, 10: 10, A: "A", B: "B", a: "a", b: "b"}
*/

export const orderObjectKeys = object => {
  try {
    return Object
      .keys(object)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = object[key]
        return accumulator
      }, {})
  } catch (e) {
    console.warn('Couldn\'t sort object.')
    return object
  }
}

export const findTallest = elementsOrSelector => {
  let tallest = null
  const isElements = NodeList.prototype.isPrototypeOf.call(elementsOrSelector) || typeof elementsOrSelector !== 'string'
  let elements = isElements ? elementsOrSelector : document.querySelectorAll(elementsOrSelector)
  elements = toArray(elements)

  if (!elements || !elements.length) return tallest

  tallest = Math.max(...elements.map(el => el.offsetHeight))

  tallest = elements.filter(el => el.offsetHeight === tallest)[0]

  return tallest
}

export const findAvailableHeight = (typeof window !== 'undefined' ? window : {}).findAvailableHeight = (el) => {
  if (!el) return 0
  const originalHeight = el.style.height
  el.style.height = 0
  const parent = el.parentElement
  const parentStyle = getComputedStyle(parent, null)
  const parentHeight = parent.clientHeight - parseFloat(parentStyle.getPropertyValue('padding-top')) - parseFloat(parentStyle.getPropertyValue('padding-bottom'))

  const siblingsHeight = Array
    .from(parent.children)
    .reduce((height, sibling) => {
      if (sibling !== el) {
        const siblingStyle = getComputedStyle(sibling)

        if ((!~['absolute', 'fixed'].indexOf(siblingStyle.position))) {
          return (height + (sibling.clientHeight + parseFloat(siblingStyle.getPropertyValue('margin-top')) + parseFloat(siblingStyle.getPropertyValue('margin-bottom'))))
        }
      }

      return height
    }, 0)

  el.style.height = originalHeight
  return (parentHeight - siblingsHeight)
}

export const getStackTrace = () => {
  const err = new Error()
  return err.stack
}

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export const toCamelCase = str => str.toLowerCase().replace(/\W+(.)/g, (_, char) => char.toUpperCase())

export const camelCaseToDashed = str => str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)

export const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

export const omniStorage = {
  prefix: 'OMNI_',
  prefixed (prefix = '') {
    return { ...this, prefix }
  },
  stores: typeof window !== 'undefined' ? {
    // cookieStorage: (() => {
    //   if (!window.hasOwnProperty('cookieStorage')) {
    //     Object.defineProperty(window, 'cookieStorage', {
    //       enumerable: true,
    //       get: () => {
    //         const cookies = getCookies()
    //         Object.defineProperty(cookies, 'getItem', {
    //           value: getCookie
    //         })
    //         Object.defineProperty(cookies, 'setItem', {
    //           value: setCookie
    //         })
    //         Object.defineProperty(cookies, 'removeItem', {
    //           value: removeCookie
    //         })
    //         return cookies
    //       }
    //     })
    //   }
    //
    //   return window.cookieStorage
    // })(),
    localStorage
    // sessionStorage
  } : {},
  shouldConvertValues: true,
  returnValue (value) {
    try {
      return this.shouldConvertValues ? JSON.parse(value) : value
    } catch (e) {
      return value
    }
  },
  get (key) {
    if (typeof key === 'undefined') return
    let value
    const values = []

    for (const store in this.stores) {
      if (this.stores[store]) {
        const value = this.stores[store].getItem(this.prefix + key)

        if (value !== null && value !== undefined) {
          values.push(value)
        }
      }
    }

    const highestOccurrence = values.sort((a, b) => {
      return values.filter(v => v === a).length -
        values.filter(v => v === b).length
    }
    ).pop()

    if (![highestOccurrence, typeof highestOccurrence].includes('undefined')) {
      if (values.some(value => value !== highestOccurrence)) {
        value = this.set(key, highestOccurrence)
      } else {
        value = highestOccurrence
      }

      return this.returnValue(value)
    } else {
      return undefined
    }
  },
  getItem (key) {
    return this.get(key)
  },
  set (key, value) {
    if ([typeof key].includes('undefined')) return
    if (this.shouldConvertValues && typeof value === 'object') {
      try {
        value = JSON.stringify(value)
      } catch (e) {
        // ignore
      }
    }

    for (const store in this.stores) {
      if (this.stores[store]) {
        this.stores[store].setItem(this.prefix + key, value)
      }
    }

    return this.returnValue(value)
  },
  setItem (key, value) {
    return this.set(key, value)
  },
  remove (key) {
    if (typeof key === 'undefined') return
    for (const store in this.stores) {
      if (this.stores[store]) {
        this.stores[store].removeItem(this.prefix + key)
      }
    }

    return true
  },
  removeItem (key) {
    return this.remove(key)
  },
  allKeys () {
    let allKeys = []
    for (const store in this.stores) {
      if (this.stores[store]) {
        allKeys = allKeys.concat(
          Object.keys(this.stores[store]).filter(key => {
            return !key.indexOf(this.prefix)
          })
        )
      }
    }

    return allKeys.filter((key, i, array) => {
      return array.indexOf(key) === i
    })
  },
  getAll () {
    return this.allKeys().reduce((all, key) => {
      all[key] = this.get(key.replace(this.prefix, ''))
      return all
    }, {})
  },
  removeAll () {
    return this.allKeys().map(key => {
      this.remove(key.replace(this.prefix, ''))
      return key
    })
  }
}

export const storage = omniStorage.prefixed('')

export const speechTest = () => {
  var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
  var SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList
  // var SpeechRecognitionEvent = window.SpeechRecognitionEvent || window.webkitSpeechRecognitionEvent

  var commands = [
    'engage quantum',
    'flight',
    'ground',
    'combat',
    'social',
    'shards',
    'home'
  ]
  var grammar = '#JSGF V1.0; grammar commands; public <command> = ' + commands.join(' | ') + ' ;'

  var recognition = new SpeechRecognition()
  var speechRecognitionList = new SpeechGrammarList()
  speechRecognitionList.addFromString(grammar, 1)
  recognition.grammars = speechRecognitionList
  // recognition.continuous = false;
  recognition.lang = 'en-US'
  recognition.interimResults = false
  recognition.maxAlternatives = 1

  recognition.onresult = function (event) {
  // The SpeechRecognitionEvent results property returns a SpeechRecognitionResultList object
  // The SpeechRecognitionResultList object contains SpeechRecognitionResult objects.
  // It has a getter so it can be accessed like an array
  // The [last] returns the SpeechRecognitionResult at the last position.
  // Each SpeechRecognitionResult object contains SpeechRecognitionAlternative objects that contain individual results.
  // These also have getters so they can be accessed like arrays.
  // The [0] returns the SpeechRecognitionAlternative at position 0.
  // We then return the transcript property of the SpeechRecognitionAlternative object

    var last = event.results.length - 1
    var command = event.results[last][0].transcript
    console.log('Confidence: ' + event.results[0][0].confidence)
    console.log(event)

    var triggerMouseEvent = function (node, eventType) {
      var clickEvent = document.createEvent('MouseEvents')
      clickEvent.initEvent(eventType, true, true)
      node.dispatchEvent(clickEvent)
    }

    // --- Get the first link that has "stackoverflow" in its URL.
    var targetNode = Array.from(document.querySelectorAll('body *'))
      .reverse()
      .find(el => 'innerText' in el ? (el.innerText && el.innerText.toLowerCase().trim() === command.toLowerCase()) : ((el.innerHTML && el.innerHTML.toLowerCase().trim() === command.toLowerCase())))

    // targetNode = temp2
    console.log(targetNode)
    if (targetNode) {
    // --- Simulate a natural mouse-click sequence.
      triggerMouseEvent(targetNode, 'mouseover')
      triggerMouseEvent(targetNode, 'mousedown')
      triggerMouseEvent(targetNode, 'mouseup')
      triggerMouseEvent(targetNode, 'click')
    } else {
      console.log('*** Target node not found!')
    }
  }

  recognition.onspeechend = function () {
    recognition.stop()
  }

  recognition.onnomatch = function () {
    // diagnostic.textContent = "I didn't recognise that color."
  }

  recognition.onerror = function () {
    // diagnostic.textContent = 'Error occurred in recognition: ' + event.error
  }

  recognition.start()
}

export const hexToRgb = (hex) => {
  hex = hex.replace(/[^0-9A-F]/gi, '')

  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `${r},${g},${b}`
}

export const rgbToHex = (rgb) => {
  const [r, g, b] = rgb.split(',').map(Number)

  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

export const isEquivalent = (a, b) => {
  // Create arrays of property names
  var aProps = Object.getOwnPropertyNames(a)
  var bProps = Object.getOwnPropertyNames(b)

  // If number of properties is different,
  // objects are not equivalent
  if (aProps.length !== bProps.length) {
    return false
  }

  for (var i = 0; i < aProps.length; i++) {
    var propName = aProps[i]

    // If values of same property are not equal,
    // objects are not equivalent
    if (a[propName] !== b[propName]) {
      return false
    }
  }

  // If we made it this far, objects
  // are considered equivalent
  return true
}

// check to see if item is a function
export const isFunction = (functionToCheck) => {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]'
}

export class EzCrypto {
  constructor (salt) {
    this.encrypt = this._cipher(salt)
    this.decrypt = this._decipher(salt)
  }

  _cipher = salt => {
    const textToChars = text => text.split('').map(c => c.charCodeAt(0))
    const byteHex = n => ('0' + Number(n).toString(16)).substr(-2)
    const applySaltToChar = code => textToChars(salt).reduce((a, b) => a ^ b, code)

    return text => text.split('')
      .map(textToChars)
      .map(applySaltToChar)
      .map(byteHex)
      .join('')
  }

  _decipher = salt => {
    const textToChars = text => text.split('').map(c => c.charCodeAt(0))
    const saltChars = textToChars(salt)
    const applySaltToChar = code => saltChars.reduce((a, b) => a ^ b, code)
    return encoded => encoded.match(/.{1,2}/g)
      .map(hex => parseInt(hex, 16))
      .map(applySaltToChar)
      .map(charCode => String.fromCharCode(charCode))
      .join('')
  }
}

export const isMobile = typeof window !== 'undefined' && (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(navigator.userAgent||navigator.vendor||window.opera)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test((navigator.userAgent||navigator.vendor||window.opera).substring(0,4)))

export const isIOS = typeof window !== 'undefined' && /(iPad|iPhone|iPod)/g.test(navigator.userAgent)